<template>
  <!-- Row: Chart -->
  <div class="row f-row quantity_chart_height">
    <div class="col col-12" style="height:100%; width:100%">
      <div
        class="h-100 d-flex flex-column"
        id="planning-div"
        style="width: 100%"
      >
        <svg id="svg-planning"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorExpo,
  colorHedge,
  colorHedgeAlpha2,
  colorNet,
  colorNet_2,
  colorNetAlpha_2,
  colorSymmetryAlpha3,
  currencyFormat,
  darkFwdAlpha4,
  darkFwdF,
  formatPercentSm,
  numberFormat,
  textactive,
  formatNumber,
  formatPercent,
  formatM
} from "@/_core/_helpers/_parametric_tools";
import { format, select } from "d3";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";

export default {
  name: "QtyChart",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics()
    },
    colorTheme: { required: true, type: Boolean, default: true },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 }
  },
  components: {},
  data() {
    return {
      D3Chart: null,
      D3HoverPlanning: null,
      dateFormat: "%b %y", // TODO: que cambie
      expandFRow: false, // TODO: que cambie
      svgNode: null,
      minMaxYAxis: [Infinity, -Infinity],
      currencyFormat: format(currencyFormat),
      chartBodyPlanning: null,
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false
        }
      },
      isUpdatingTable: false
    };
  },
  created() {
    this.D3Chart = new D3Chart("planning-div", "svg-planning");
    this.D3HoverPlanning = new D3HoverPlanning();
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    renderChartSignal() {
      if (this.Analytics["rawData"]["zoomedData"].length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    formatVars: {
      handler() {
        if (!this.isUpdatingTable) {
          this.isUpdatingTable = true;
          setTimeout(() => {
            this.isUpdatingTable = false;
            this.onHoverChart();
          });
        }
      },
      deep: true
    }
  },
  methods: {
    deleteChart() {
      select("#planning-div")
        .selectAll(".tooltip")
        .remove();
      select("#planning-div")
        .select("svg > *")
        .remove();
    },
    createBarChart() {
      if (this.Analytics["rawData"]["zoomedData"].length === 0) {
        return;
      }
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        10,
        0.5,
        55,
        0.5,
        20,
        0.5,
        1,
        true
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      if (this.Analytics["userSelections"]["normalizeQty"]) {
        this.minMaxYAxis = [0, 1];
        if (this.Analytics["userSelections"]["showBudget"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "budgetQNorm",
            "budgetQNorm",
            this.minMaxYAxis
          );
        }
        if (this.Analytics["userSelections"]["showFixedFutures"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "fixedHedgedNorm",
            "fixedHedgedNorm",
            this.minMaxYAxis
          );
        }
        if (this.Analytics["userSelections"]["showFixedBasis"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "ratioQNorm",
            "ratioQNorm",
            this.minMaxYAxis
          );
        }
      } else {
        this.minMaxYAxis = [Infinity, -Infinity];
        if (this.Analytics["userSelections"]["showBudget"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "budgetQ",
            "budgetQ",
            this.minMaxYAxis
          );
        }
        if (this.Analytics["userSelections"]["showForecast"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "forecastQ",
            "forecastQ",
            this.minMaxYAxis
          );
        }
        if (this.Analytics["userSelections"]["showFixedFutures"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "fixedHedged",
            "fixedHedged",
            this.minMaxYAxis
          );
        }
        if (this.Analytics["userSelections"]["showFixedBasis"]) {
          this.minMaxYAxis = this.processData.minMax(
            this.Analytics["rawData"]["zoomedData"],
            "ratioQ",
            "ratioQ",
            this.minMaxYAxis
          );
        }
      }
      this.minMaxYAxis = [
        Math.min(0, this.minMaxYAxis[0]),
        Math.max(0, this.minMaxYAxis[1])
      ];
      let del = 0.1 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      let del1 = this.minMaxYAxis[0] === 0 ? 0 : del;
      let del2 = this.minMaxYAxis[1] === 0 ? 0 : del;
      this.minMaxYAxis = [
        Math.min(0, this.minMaxYAxis[0] - del1),
        Math.max(0, this.minMaxYAxis[1] + del2)
      ];
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"],
        this.minMaxYAxis,
        this.dateFormat,
        50,
        4,
        false,
        [],
        true,
        true,
        true,
        this.Analytics["userSelections"]["normalizeQty"]
          ? formatPercentSm
          : numberFormat,
        false
      );

      // ==== zoom setup
      this.chartBodyPlanning = this.svgNode
        .append("g")
        .attr("class", "chartBodyPlanning")
        .attr("clip-path", `url(#clipPlanning)`);
      this.svgNode
        .append("defs")
        .append("clipPath")
        .attr("id", "clipPlanning")
        .append("rect")
        .attr("width", this.svgNode.width)
        .attr("height", this.svgNode.height);

      if (!this.expandFRow) {
        del = 0.03 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      } else {
        del = 0.1 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      }

      // const barWidth = 0.7;
      // if (this.isFuture) {
      this.D3Chart.backgroundRect(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"].map(d => d.date),
        this.Analytics["rawData"]["zoomedData"].map((d, i) => i % 2 !== 0),
        "PLAN",
        0,
        0,
        this.chartBodyPlanning
      );
      // }
      if (this.Analytics["userSelections"]["showForecast"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.7,
          "PLANB",
          colorExpo(this.colorTheme),
          colorSymmetryAlpha3(this.colorTheme),
          "date",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "forecastQNorm"
            : "forecastQ",
          true,
          false,
          true,
          2,
          1,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyPlanning
        );
      }
      // if (this.showRequired && !this.expandFRow) {
      //   this.Analytics["rawData"]["zoomedData"].forEach(function(d) {
      //     d.hedgeExpand = d.hedge - d.last_m_hedge;
      //     d.hedgeNExpand = d.fixedQNorm - d.last_m_hedgeN;
      //     d.required =
      //       d.program === 0
      //         ? 0
      //         : d.program > 0
      //         ? Math.max(d.program - d.hedge, 0)
      //         : -Math.max(d.hedge - d.program, 0);
      //     d.requiredN =
      //       d.programN === 0
      //         ? 0
      //         : d.programN >= 0
      //         ? Math.max(d.programN - d.fixedQNorm, 0)
      //         : -Math.max(d.fixedQNorm - d.programN, 0);
      //     d.requiredPPos = d.hedge > 0 ? d.hedge + del : d.hedge - del;
      //     d.requiredPPosN = d.fixedQNorm > 0 ? d.fixedQNorm + del : d.fixedQNorm - del;
      //     d.requiredP =
      //       d.tot !== 0 && d.required !== 0
      //         ? formatPercentSm(d.required / d.tot)
      //         : "";
      //   });
      //   this.D3Chart.twoStackedBars(
      //     this.svgNode,
      //     this.Analytics["rawData"]["zoomedData"],
      //     0.5,
      //     "REQH",
      //     "REQH2",
      //     "rgba(0,0,0,0)",
      //     "rgba(0,0,0,0)",
      //     whiteGray(this.colorTheme),
      //     whiteGrayAlpha(this.colorTheme),
      //     "date",
      //     this.Analytics["userSelections"]["normalizeQty"] ? "fixedQNorm" : "hedge",
      //     this.Analytics["userSelections"]["normalizeQty"]
      //       ? "requiredN"
      //       : "required",
      //     true,
      //     1
      //   );
      // }
      if (this.Analytics["userSelections"]["showFixedBasis"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.25,
          "PLANP",
          colorHedge(this.colorTheme),
          colorHedgeAlpha2(this.colorTheme),
          "date",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "ratioQNorm"
            : "ratioQ",
          true,
          false,
          true,
          2,
          1,
          true,
          "date",
          false,
          "",
          0.24,
          false,
          0,
          this.chartBodyPlanning
        );
      }
      if (this.Analytics["userSelections"]["showFixedFutures"]) {
        // if (!this.expandFRow) {
        //   this.D3Chart.twoStackedBars(
        //     this.svgNode,
        //     this.Analytics["rawData"]["zoomedData"],
        //     0.5,
        //     "PLANH",
        //     "PLANH2",
        //     colorHedge(this.colorTheme),
        //     colorHedgeAlpha2(this.colorTheme),
        //     darkFwd,
        //     darkFwdAlpha3,
        //     "date",
        //     this.Analytics["userSelections"]["normalizeQty"]
        //       ? "hedgeNExpand"
        //       : "hedgeExpand",
        //     this.Analytics["userSelections"]["normalizeQty"]
        //       ? "last_m_hedgeN"
        //       : "last_m_hedge",
        //     true,
        //     1
        //   );
        // } else {
        this.D3Chart.twoStackedBars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.25,
          "PLANH",
          "PLANHH",
          darkFwdF(this.colorTheme),
          darkFwdAlpha4(this.colorTheme),
          colorNet_2(this.colorTheme),
          colorNetAlpha_2(this.colorTheme),
          "date",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "fixedQNorm"
            : "fixedQ",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "hedgedQNorm"
            : "hedgedQ",
          true,
          1,
          0.51,
          this.chartBodyPlanning,
          true,
          "date"
        );

        // this.D3Chart.bars(
        //   this.svgNode,
        //   this.Analytics["rawData"]["zoomedData"],
        //   0.25,
        //   "PLANH",
        //   darkFwd,
        //   darkFwdAlpha4(this.colorTheme),
        //   "date",
        //   this.Analytics["userSelections"]["normalizeQty"]
        //     ? "fixedQNorm"
        //     : "fixedQ",
        //   true,
        //   false,
        //   true,
        //   2,
        //   1,
        //   true,
        //   "id",
        //   false,
        //   "",
        //   0.51
        // );
        //
        // this.D3Chart.bars(
        //   this.svgNode,
        //   this.Analytics["rawData"]["zoomedData"],
        //   0.25,
        //   "PLANHH",
        //   colorNet_2(this.colorTheme),
        //   colorNetAlpha_2(this.colorTheme),
        //   "date",
        //   this.Analytics["userSelections"]["normalizeQty"]
        //     ? "hedgedQNorm"
        //     : "hedgedQ",
        //   true,
        //   false,
        //   true,
        //   2,
        //   1,
        //   true,
        //   "id",
        //   false,
        //   "",
        //   0.51
        // );

        // }
      }
      if (this.Analytics["userSelections"]["showBudget"]) {
        this.D3Chart.bars(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          0.9,
          "PLANB2",
          colorExpo(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "budgetQNorm"
            : "budgetQ",
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
          false,
          "",
          0,
          false,
          0,
          this.chartBodyPlanning
        );
      }

      let normalize = this.Analytics["userSelections"]["normalizeQty"];
      this.Analytics["rawData"]["zoomedData"].forEach(function(d) {
        if (normalize) {
          d.programN =
            d.programN === 0 && d.balance + d.cash_flow === 0
              ? -Infinity
              : d.programN;
        }
        d.program =
          d.program === 0 && d.balance + d.cash_flow === 0
            ? -Infinity
            : d.program;
      });

      let objTexts = {
        xField: "date",
        yFields: [],
        customNameTexts: [],
        data: this.Analytics["rawData"]["zoomedData"],
        xOffsets: []
      };
      // if (this.showProgram) {
      //   this.D3Chart.bars(
      //     this.svgNode,
      //     this.Analytics["rawData"]["zoomedData"],
      //     0.75,
      //     "PLANP",
      //     colorNet(this.colorTheme),
      //     "rgba(0,0,0,0)",
      //     "date",
      //     this.Analytics["userSelections"]["normalizeQty"]
      //       ? "programN"
      //       : "program",
      //     true,
      //     false,
      //     false,
      //     2,
      //     1,
      //     true,
      //     "id"
      //   );
      // }
      // if (
      //   !this.expandFRow &&
      //   this.showRequired &&
      //   this.Analytics["rawData"]["zoomedData"].length < 25
      // ) {
      //   this.D3Chart.text(
      //     this.svgNode,
      //     this.Analytics["rawData"]["zoomedData"],
      //     "date",
      //     this.Analytics["userSelections"]["normalizeQty"]
      //       ? "requiredPPosN"
      //       : "requiredPPos",
      //     "requiredP",
      //     "reqTxt",
      //     "11px",
      //     this.svgNode.x.bandwidth() / 2,
      //     0,
      //     "middle",
      //     "central",
      //     0,
      //     textactive2(this.colorTheme)
      //   );
      // }
      if (
        this.showProgram &&
        !this.expandFRow &&
        this.Analytics["rawData"]["zoomedData"].length < 25
      ) {
        this.D3Chart.text(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          "date",
          this.Analytics["userSelections"]["normalizeQty"]
            ? "programPosN"
            : "programPos",
          "programP",
          "progTxt",
          "11px",
          this.svgNode.x.bandwidth() / 2,
          0,
          "middle",
          "central",
          0,
          this.colorTheme
            ? colorNet(this.colorTheme)
            : textactive(this.colorTheme),
          true,
          this.chartBodyPlanning
        );
        objTexts.yFields.push(
          this.Analytics["userSelections"]["normalizeQty"]
            ? "programPosN"
            : "programPos"
        );
        objTexts.customNameTexts.push("progTxt");
        objTexts.xOffsets.push(this.svgNode.x.bandwidth() / 2);
      }
      if (
        this.Analytics["userSelections"]["showFixedBasis"] &&
        this.Analytics["rawData"]["zoomedData"].length < 16
      ) {
        this.D3Chart.text(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          "date",
          "zeroFld",
          "ratioQPercS",
          "hedgeP",
          "11px",
          this.svgNode.x.bandwidth() / 2 - 2,
          0,
          "end",
          "ideographic",
          0,
          textactive(this.colorTheme),
          true,
          this.chartBodyPlanning
        );
        objTexts.yFields.push("zeroFld");
        objTexts.customNameTexts.push("hedgeP");
        objTexts.xOffsets.push(this.svgNode.x.bandwidth() / 2 - 2);
      }

      if (
        this.Analytics["userSelections"]["showFixedFutures"] &&
        this.Analytics["rawData"]["zoomedData"].length < 16
      ) {
        this.D3Chart.text(
          this.svgNode,
          this.Analytics["rawData"]["zoomedData"],
          "date",
          "zeroFld",
          "fixedQPercS",
          "hedgeP1",
          "11px",
          this.svgNode.x.bandwidth() / 2 + 2,
          0,
          "start",
          "ideographic",
          0,
          textactive(this.colorTheme),
          true,
          this.chartBodyPlanning
        );
        objTexts.yFields.push("zeroFld");
        objTexts.customNameTexts.push("hedgeP1");
        objTexts.xOffsets.push(this.svgNode.x.bandwidth() / 2 + 2);
      }
      // x axis BUY SELL label
      if (!this.Analytics["userSelections"]["normalizeQty"]) {
        if (this.minMaxYAxis[0] < 0) {
          this.svgNode
            .append("g")
            .append("text")
            .attr("y", 6)
            .attr("dy", ".71em")
            .attr("class", "axis-label")
            .attr(
              "transform",
              `translate(-15, ${this.svgNode.height - 2}) rotate(-90)`
            )
            .style("text-anchor", "start")
            .style("dominant-baseline", "ideographic")
            .style("font-size", "0.85rem")
            .text("BUY");
        }
        if (this.minMaxYAxis[1] > 0) {
          this.svgNode
            .append("g")
            .append("text")
            .attr("y", 6)
            .attr("dy", ".71em")
            .attr("class", "axis-label")
            .attr("transform", `translate(-15, +4) rotate(-90)`)
            .style("text-anchor", "end")
            .style("dominant-baseline", "ideographic")
            .style("font-size", "0.85rem")
            .text("SELL");
        }
      }

      // === Configuration hover elements
      const objPlanB = {
        showFields: [
          "showForecast",
          "showFixedBasis",
          "showBudget",
          "showFixedFutures",
          "showFixedFutures"
        ],
        data: this.Analytics["rawData"]["zoomedData"],
        customNameBars: [
          "PLANB",
          "PLANP",
          "PLANB2",
          "stackedBar1",
          "stackedBar2"
        ],
        yFields: [
          ["forecastQNorm", "forecastQ"],
          ["ratioQNorm", "ratioQ"],
          ["budgetQNorm", "budgetQ"],
          ["fixedQNorm", "fixedQ"],
          ["hedgedQNorm", "hedgedQ"]
        ],
        multipleYField: "normalizeQty"
      };
      const dataBars = [objPlanB];
      const dataTexts = [objTexts];
      const figuresElemsHover = [
        { type: "bars", data: dataBars },
        { type: "texts", data: dataTexts }
      ];
      // === hover fx ===
      this.D3HoverPlanning.hoverFct(
        this.svgNode,
        this.Analytics["rawData"]["zoomedData"],
        "PLAN",
        formatNumber,
        this.currency,
        this.Analytics["userSelections"]["normalizeQty"]
          ? formatPercent
          : numberFormat,
        this.dateFormat,
        this.currencyFormat,
        this.budgetRate,
        formatPercent,
        // this.showTooltip,
        false,
        formatM,
        this.averages,
        false,
        this.asset,
        this.isFuture,
        this.colorTheme,
        true,
        this,
        "date",
        true,
        figuresElemsHover,
        "render",
        this.Analytics["rawData"],
        true
      );
    },
    onHoverChart() {
      this.$emit("onTableFormat", this.formatVars.userHovered);
    },
    callWidth() {
      if (this.Analytics["rawData"]["zoomedData"].length > 0) {
        this.createBarChart();
      }
    }
  }
};
</script>

<style scoped></style>
